// Greedy — curated interview problem with multiple worked approaches.
// Same shape as dsaProblems/dp2d.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer for the optimized approaches.

const greedyProblems = [
  {
    id: 'jump-game',
    title: 'Jump Game',
    difficulty: 'Medium',
    leetcode: { number: 55, url: 'https://leetcode.com/problems/jump-game/' },
    statement: 'Given an array where each element is the maximum jump length from that position, starting at index 0, determine whether the last index is reachable.',
    example: {
      input: 'nums = [2, 3, 1, 1, 4]',
      output: 'true',
      note: 'One valid path: index 0 → index 1 (jump 1) → index 4 (jump 3).',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "From each position, recursively try every possible jump distance, and succeed if ANY of them leads " +
          "to eventually reaching the last index. Correct, but the same position gets reached — and fully " +
          "re-explored — by many different earlier jump sequences.",
        steps: [
          'Base case: standing on the last index is trivially success.',
          'From the current position, try every jump distance from 1 up to the maximum allowed there.',
          'Recursively check whether the last index is reachable from each resulting position.',
          'Succeed the moment ANY branch succeeds.',
        ],
        code:
`boolean canJumpBruteForce(int[] nums, int pos) {
    if (pos == nums.length - 1) return true;
    int furthestJump = Math.min(pos + nums[pos], nums.length - 1);
    for (int nextPos = pos + 1; nextPos <= furthestJump; nextPos++) {
        if (canJumpBruteForce(nums, nextPos)) return true;
    }
    return false;
}`,
        complexity: { time: 'O(2ⁿ) worst case', space: 'O(n) — recursion depth' },
        whenToUse: "Only useful to confirm the recurrence directly — the same position is reachable via many different jump sequences, all independently re-explored without memoization.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up DP',
        why:
          "Build dp[i] — true if index i is reachable at all — forward. Each dp[i] only needs to check whether " +
          "SOME earlier reachable index j could jump far enough to land on (or past) i. This formalizes " +
          "\"reachability\" as a table, removing the brute force's redundant re-exploration.",
        steps: [
          'dp[0] = true — the start is always reachable.',
          'For each index i, scan every earlier index j: if dp[j] is true and j + nums[j] reaches at least i, then dp[i] is true.',
          'Stop scanning for a given i the moment one qualifying j is found.',
          'Return dp[n-1].',
        ],
        visual: {
          arrays: [
            { label: 'nums', values: [2, 3, 1, 1, 4] },
            { label: 'dp[0..4] (T = index reachable)', values: ['T'] },
          ],
          varNames: [],
          steps: [
            { snapshots: { 'dp[0..4] (T = index reachable)': ['T', 'T'] }, highlights: { 'dp[0..4] (T = index reachable)': [1] }, note: 'i=1: j=0 works (0 + nums[0]=2 ≥ 1) → dp[1] = true.' },
            { snapshots: { 'dp[0..4] (T = index reachable)': ['T', 'T', 'T'] }, highlights: { 'dp[0..4] (T = index reachable)': [2] }, note: 'i=2: j=0 works (2 ≥ 2) → dp[2] = true.' },
            { snapshots: { 'dp[0..4] (T = index reachable)': ['T', 'T', 'T', 'T'] }, highlights: { 'dp[0..4] (T = index reachable)': [3] }, note: 'i=3: j=0 fails (2 ≥ 3? no); j=1 works (1+3=4 ≥ 3) → dp[3] = true.' },
            { snapshots: { 'dp[0..4] (T = index reachable)': ['T', 'T', 'T', 'T', 'T'] }, highlights: { 'dp[0..4] (T = index reachable)': [4] }, note: 'i=4: j=1 works (1+3=4 ≥ 4) → dp[4] = true — final answer: true.' },
          ],
        },
        code:
`boolean canJumpDP(int[] nums) {
    int n = nums.length;
    boolean[] dp = new boolean[n];
    dp[0] = true;
    for (int i = 1; i < n; i++) {
        for (int j = 0; j < i; j++) {
            if (dp[j] && j + nums[j] >= i) {
                dp[i] = true;
                break;
            }
        }
    }
    return dp[n - 1];
}`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
        whenToUse: "A useful stepping stone that formalizes reachability as a table — but it still recomputes information the greedy approach below tracks with a single rolling variable, in linear time instead of quadratic.",
      },
      {
        name: 'Approach 3 — Further Optimized: Greedy (Farthest Reach)',
        why:
          "The DP table only ever needs ONE piece of information from all reachable indices so far: the single " +
          "FARTHEST one any of them can jump to. A bigger farthest reach can never make a later decision worse " +
          "— it only ever helps — which is exactly what makes tracking just that one rolling value sufficient, " +
          "and provably correct (an exchange argument, covered in this pattern's course content).",
        steps: [
          'Track a single running value: farthest, the best index reachable using every position visited so far.',
          'At each index i, first check: has the walk already outrun farthest? If i > farthest, this index was never reachable — fail immediately.',
          'Otherwise, update farthest = max(farthest, i + nums[i]).',
          'If the loop completes without ever failing, the last index was always within reach — return true.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [2, 3, 1, 1, 4] }],
          varNames: ['farthest'],
          steps: [
            { highlights: { nums: [0] }, vars: { farthest: 2 }, note: 'i=0: farthest = max(0, 0+2) = 2.' },
            { highlights: { nums: [1] }, vars: { farthest: 4 }, note: 'i=1: 1 ≤ farthest(2), still reachable. farthest = max(2, 1+3) = 4.' },
            { highlights: { nums: [2] }, vars: { farthest: 4 }, note: 'i=2: 2 ≤ farthest(4). farthest = max(4, 2+1) = 4 (no improvement).' },
            { highlights: { nums: [3] }, vars: { farthest: 4 }, note: 'i=3: 3 ≤ farthest(4). farthest = max(4, 3+1) = 4 (no improvement).' },
            { highlights: { nums: [4] }, vars: { farthest: 8 }, note: 'i=4: 4 ≤ farthest(4) — the last index is already reachable! farthest = max(4, 4+4) = 8. Loop ends: answer is true.' },
          ],
        },
        code:
`boolean canJump(int[] nums) {
    int farthest = 0;
    for (int i = 0; i < nums.length; i++) {
        if (i > farthest) return false;
        farthest = Math.max(farthest, i + nums[i]);
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Try the \"Jump Game\" mode in this pattern's interactive playground with any jump-length array to watch farthest decide everything in a single pass.",
        whenToUse: "The canonical greedy solution, and the one expected in an interview — recognizing that a DP table's dependency collapses to a single running value (rather than a full scan over all prior states) is the core skill this entire pattern trains.",
      },
    ],
  },
];

export default greedyProblems;
